import React, { useMemo }           from 'react';
import {
  Form,
  Field,
  useFormikContext,
}                                   from 'formik';
import { createStyles, withStyles } from '@material-ui/styles';
import Container                    from '@material-ui/core/Container';
import { IInvoicesEmailsPayload }   from '@typings/accountManagement';
import {
  CheckboxFieldGroup,
  ICheckboxOption,
}                                   from '@components/Checkbox/CheckboxFieldGroup';
import {
  Button,
  Switch,
  TextInput,
}                                   from '@components/index';

const ButtonsWrapper = withStyles(({ spacing, breakpoints }) => createStyles({
  root: {
    display        : 'flex',
    justifyContent : 'space-evenly',
    marginBottom   : spacing(1.25),
    marginTop      : spacing(3),

    [breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
}))(Container);

interface IProps {
  invoicesEmailOptions : ICheckboxOption[];
  isLoading            : boolean;
  onCancel             : () => void;
  onSubmit             : (data: IInvoicesEmailsPayload) => Promise<void>;
}

interface ISendInvoiceFormValues {
  email                 : string;
  isWithAdditionalEmail : boolean;
  invoicesEmails        : Record<string, boolean>;
}

export const SendInvoiceForm: React.FC<IProps> = ({
  invoicesEmailOptions,
  isLoading,
  onCancel,
  onSubmit,
}) => {
  const {
    values,
    errors,
    setFieldValue,
    isSubmitting,
  } = useFormikContext<ISendInvoiceFormValues>();

  const invoiceError = useMemo(
    () => ((Object.keys(values.invoicesEmails)
      .filter((key) => values.invoicesEmails[key]).length
      || (values.email && values.isWithAdditionalEmail))
      ? ''
      : 'Select al least one email'),
    [values],
  );

  const invoiceEmails = useMemo(
    () => invoicesEmailOptions
      .map((item, index) => ((values.invoicesEmails[index]) ? item.label : ''))
      .filter((email) => !!email),
    [invoicesEmailOptions, values.invoicesEmails],
  );

  return (
    <Form>
      <CheckboxFieldGroup
        bulkLabel     = "Emails"
        groupName     = "invoicesEmails"
        options       = {invoicesEmailOptions}
        values        = {values.invoicesEmails}
        setFieldValue = {setFieldValue}
      />

      <Field
        id        = "isWithAdditionalEmail"
        name      = "isWithAdditionalEmail"
        label     = "Additional Email"
        component = {Switch}
      />

      {values.isWithAdditionalEmail && (
        <>
          <Field
            id          = "email"
            name        = "email"
            type        = "email"
            placeholder = "Email"
            stripChars  = "[^a-zA-Z0-9@._+\-\s]*"
            inputProps  = {{ maxLength: 150 }}
            error       = {!!errors.email}
            component   = {TextInput}
            isInvoice
          />

          <div style={{ fontSize: '12px' }}>
            When adding more than one email, separate each email address using a space.
          </div>
        </>
      )}

      <ButtonsWrapper>
        <Button
          label   = "Cancel"
          variant = "outlined"
          width   = "180px"
          onClick = {onCancel}
        />
        <Button
          label     = "Send"
          width     = "180px"
          isLoading = {isLoading || isSubmitting}
          disabled  = {isLoading
            || isSubmitting
            || !!invoiceError
            || !!(values.isWithAdditionalEmail && errors.email)}
          onClick   = {() => onSubmit({
            invoiceEmails,
            additionalEmail: values.isWithAdditionalEmail ? values.email : '',
          })}
        />
      </ButtonsWrapper>
    </Form>
  );
};
